/**
 * referenceGuide.ts — Complete product reference guide content
 *
 * Comprehensive user guide + integration guide covering every feature
 * of the Rule42 Billing platform. Used by the IntegrationDocsPage
 * for the full reference documentation experience.
 *
 * @module packages/billing-admin/src/guides/referenceGuide
 * @since 2026-06-20
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface GuideCodeBlock {
  language: string;
  code: string;
  file?: string;
  caption?: string;
}

export interface GuideSection {
  id: string;
  title: string;
  content: string;
  codeBlocks?: GuideCodeBlock[];
}

export interface GuideChapter {
  id: string;
  title: string;
  icon: string;
  description: string;
  sections: GuideSection[];
}

export interface GuideCategory {
  id: string;
  title: string;
  icon?: string;
  chapters: GuideChapter[];
}

// ---------------------------------------------------------------------------
// Guide Data
// ---------------------------------------------------------------------------

export const GUIDE_CATEGORIES: GuideCategory[] = [
  // ═══════════════════════════════════════════════════════════════════════════
  // GETTING STARTED
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'getting-started',
    title: 'Getting Started',
    chapters: [
      {
        id: 'overview',
        title: 'Platform Overview',
        icon: '🏠',
        description: 'What Rule42 Billing is and how it fits into your stack.',
        sections: [
          {
            id: 'what-is',
            title: 'What is Rule42 Billing?',
            content: `Rule42 Billing is a multi-tenant, usage-based billing platform built for SaaS companies. It handles the entire billing lifecycle — from customer onboarding and plan management to usage metering, invoicing, and revenue analytics.

The platform is designed as a standalone billing service that integrates with your existing auth system and application via APIs and webhooks. It supports:

• **Usage-based billing** — charge customers based on actual consumption (API calls, compute time, storage, etc.)
• **Subscription management** — flat, per-seat, tiered, and hybrid pricing models
• **Credit system** — pre-paid balances with real-time deduction and budget controls
• **Multi-tenant isolation** — each organization gets its own billing context
• **Stripe Connect** — tenants connect their own Stripe accounts for payment processing
• **Enterprise contracts** — committed spend, volume discounts, and custom terms`,
          },
          {
            id: 'architecture',
            title: 'Architecture',
            content: `Rule42 Billing runs as a standalone service with three main integration surfaces:

**1. REST API** — Full CRUD for customers, plans, subscriptions, usage, credits, invoices
**2. Webhooks** — Bi-directional event system (receive events from your auth/app, send events to your app)
**3. MCP Server** — AI agent integration via Model Context Protocol for automated billing operations

The billing server connects to:
• **PostgreSQL** — primary data store (multi-tenant with RLS)
• **Redis** — job queues, caching, rate limiting
• **Stripe** — payment processing via Stripe Connect
• **Your Auth Service** — OIDC for admin authentication, webhooks for customer sync`,
            codeBlocks: [
              {
                language: 'text',
                caption: 'Service Architecture',
                code: `┌─────────────┐     ┌──────────────────┐     ┌─────────────┐
│  Your App   │────▶│  Rule42 Billing   │────▶│   Stripe    │
│  (client)   │◀────│  (API + Webhooks) │◀────│  (Connect)  │
└─────────────┘     └──────────────────┘     └─────────────┘
                           │    │
                    ┌──────┘    └──────┐
                    ▼                  ▼
              ┌──────────┐      ┌──────────┐
              │ Postgres │      │  Redis   │
              │  (RLS)   │      │ (queues) │
              └──────────┘      └──────────┘`,
              },
            ],
          },
          {
            id: 'quickstart',
            title: 'Quick Start Checklist',
            content: `Follow these steps to get billing integrated into your application:

1. **Connect your auth system** — Set up the auth webhook so customers are synced automatically when orgs are created in your identity provider
2. **Define event types** — Create the billable actions your app will track (API calls, file uploads, AI inferences, etc.)
3. **Create a pricing catalog** — Bundle event types with credit costs
4. **Set up plans** — Create pricing plans that reference your catalogs
5. **Report usage** — Instrument your app to send usage events via the API
6. **Configure webhooks** — Register endpoints to receive billing events (low balance alerts, invoice notifications, etc.)
7. **Monitor** — Use the admin dashboard to track revenue, usage, and customer health`,
          },
        ],
      },
      {
        id: 'authentication',
        title: 'Authentication & API Access',
        icon: '🔑',
        description: 'How to authenticate API requests and manage access tokens.',
        sections: [
          {
            id: 'api-auth',
            title: 'API Authentication',
            content: `All API requests require a Bearer token in the Authorization header. Tokens are issued by your OIDC provider (the same one used for admin login).

**Token requirements:**
• Must be a valid JWT from your configured OIDC issuer
• Admin endpoints require \`role: "admin"\` or \`org_role: "owner"\` in the token claims
• Org-scoped endpoints require the org ID to be in the token's \`org_id\` claim or passed as a URL parameter

**Rate limiting:**
• Standard endpoints: 120 requests/minute per tenant
• Platform admin endpoints: 600 requests/minute
• Usage reporting: 1000 events/minute per tenant`,
            codeBlocks: [
              {
                language: 'typescript',
                caption: 'Authenticated API request',
                code: `// All requests must include the Authorization header
const response = await fetch('https://finance.rule42.ai/api/v1/orgs/<orgId>/customers', {
  headers: {
    'Authorization': 'Bearer <YOUR_JWT_TOKEN>',
    'Content-Type': 'application/json',
  },
});`,
              },
            ],
          },
          {
            id: 'base-url',
            title: 'Base URL & Endpoints',
            content: `All API endpoints are relative to your billing server URL.`,
            codeBlocks: [
              {
                language: 'text',
                caption: 'Endpoint structure',
                code: `Base URL: https://finance.rule42.ai

API Routes:
  /api/v1/orgs/:orgId/*      — Org-scoped billing API (customers, plans, usage, etc.)
  /api/v1/platform/*         — Platform admin API (tenant management, health)
  /webhooks/stripe            — Stripe webhook receiver
  /webhooks/auth              — Auth service webhook receiver
  /api/docs/openapi.json      — OpenAPI 3.1 specification
  /api/docs/llms.txt          — AI-readable API description
  /admin/billing/             — Admin console UI
  /health                     — Health check endpoint`,
              },
            ],
          },
        ],
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // CUSTOMER MANAGEMENT
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'customers',
    title: 'Customer Management',
    chapters: [
      {
        id: 'customer-lifecycle',
        title: 'Customers & Accounts',
        icon: '👥',
        description: 'Creating, managing, and syncing customer accounts.',
        sections: [
          {
            id: 'customer-sync',
            title: 'Auto-Sync via Auth Webhooks',
            content: `The recommended approach for customer creation is automatic sync from your identity/auth service. When a new organization is created in your auth system, it fires a webhook to billing, which automatically:

1. Creates the billing customer record
2. Assigns the default plan
3. Initializes the credit balance
4. Sets up the subscription

This ensures your auth system is the source of truth for org/customer identity.

**Required webhook events to send to billing:**
• \`subscription.created\` — new org registered
• \`subscription.updated\` — plan or seat count changed
• \`member.added\` / \`member.removed\` — seat count updates`,
            codeBlocks: [
              {
                language: 'typescript',
                caption: 'Auth webhook payload (subscription.created)',
                code: `// Your auth service sends this when a new org is created:
const payload = {
  eventId: 'evt_' + crypto.randomUUID(),
  event: 'subscription.created',
  tenantId: '<ORG_UUID>',
  data: {
    org_name: 'Acme Corp',
    plan_code: 'pro',
    seat_count: 5,
    admin_email: 'admin@acme.com',
  },
};

// Sign with HMAC-SHA256
const signature = crypto
  .createHmac('sha256', WEBHOOK_SECRET)
  .update(JSON.stringify(payload))
  .digest('hex');

await fetch('https://finance.rule42.ai/webhooks/auth', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'X-Auth-Signature': signature,
  },
  body: JSON.stringify(payload),
});`,
              },
            ],
          },
          {
            id: 'customer-api',
            title: 'Create Customers via API',
            content: `Alternatively, create customers directly from your application when auth webhook sync isn't available.`,
            codeBlocks: [
              {
                language: 'typescript',
                caption: 'Create a customer',
                code: `const res = await fetch('https://finance.rule42.ai/api/v1/orgs/<orgId>/customers', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer <TOKEN>',
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    name: 'Acme Corp',
    email: 'billing@acme.com',
    metadata: { plan_code: 'pro', seat_count: 5 },
  }),
});
const customer = await res.json();
// customer.id → use this for all subsequent API calls`,
              },
            ],
          },
          {
            id: 'customer-lifecycle-events',
            title: 'Customer Lifecycle Events',
            content: `Your application should notify billing when customer state changes:

• **Plan upgrade/downgrade** — triggers proration and subscription update
• **Cancellation** — initiates the cancellation flow (access until period end)
• **Seat count change** — recalculates per-seat billing
• **Billing email change** — updates invoice delivery address
• **Customer deletion** — triggers final invoice and data retention procedures

These can be sent via webhooks or by calling the API directly.`,
          },
        ],
      },
      {
        id: 'members',
        title: 'Team Members & Seats',
        icon: '👤',
        description: 'Per-seat billing and team member management.',
        sections: [
          {
            id: 'seat-billing',
            title: 'How Seat-Based Billing Works',
            content: `For per-seat plans, the billing amount is calculated as:

**Monthly charge = Base price + (Price per seat × Active seats)**

Seat counts are updated automatically when your auth system sends \`member.added\` or \`member.removed\` webhook events. You can also update seat counts manually via the API.

Proration is handled automatically — when seats are added mid-cycle, the customer is charged the prorated amount for the remaining days in the billing period.`,
          },
          {
            id: 'seat-api',
            title: 'Update Seat Count',
            content: `Update the seat count for a customer's subscription.`,
            codeBlocks: [
              {
                language: 'typescript',
                caption: 'Update seats',
                code: `await fetch('https://finance.rule42.ai/api/v1/orgs/<orgId>/subscriptions/<subId>', {
  method: 'PATCH',
  headers: {
    'Authorization': 'Bearer <TOKEN>',
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({ seat_count: 12 }),
});`,
              },
            ],
          },
        ],
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // PLANS & PRICING
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'pricing',
    title: 'Plans & Pricing',
    chapters: [
      {
        id: 'plans',
        title: 'Plan Configuration',
        icon: '📋',
        description: 'Creating and managing pricing plans.',
        sections: [
          {
            id: 'plan-types',
            title: 'Plan Types',
            content: `Rule42 Billing supports four pricing models that can be combined:

**Flat** — Fixed monthly or annual fee (e.g., $99/month)
**Per-Seat** — Price multiplied by number of active seats (e.g., $10/seat/month)
**Tiered** — Different prices at different volume levels (e.g., first 100 API calls free, next 1000 at $0.01 each)
**Usage-Based** — Credits deducted per usage event, linked to a pricing catalog

Plans can combine these models — for example, a flat base fee of $49/month plus usage-based API call charges.`,
          },
          {
            id: 'plan-permissions',
            title: 'Plan Permissions & Entitlements',
            content: `Each plan can define feature permissions that your application checks at runtime. When a customer subscribes to a plan, they gain access to the features defined in that plan.

Use the entitlements API to gate features in your application.`,
            codeBlocks: [
              {
                language: 'typescript',
                caption: 'Check entitlements at runtime',
                code: `// In your app, check if a customer has access to a feature
const res = await fetch(
  'https://finance.rule42.ai/api/v1/orgs/<orgId>/entitlements?customerId=<UUID>',
  { headers: { 'Authorization': 'Bearer <TOKEN>' } }
);
const entitlements = await res.json();
// {
//   plan: "pro",
//   features: ["advanced_ai", "custom_protocols", "team_sharing"],
//   limits: { seats: 10, projects: 50 }
// }

if (!entitlements.features.includes('advanced_ai')) {
  throw new Error('Upgrade to Pro to access this feature');
}`,
              },
            ],
          },
          {
            id: 'plan-api',
            title: 'Plan Management API',
            content: `Create and update plans programmatically.`,
            codeBlocks: [
              {
                language: 'typescript',
                caption: 'Create a plan',
                code: `await fetch('https://finance.rule42.ai/api/v1/orgs/<orgId>/plans', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer <TOKEN>',
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    name: 'Professional',
    slug: 'pro',
    interval: 'monthly',
    base_price_cents: 9900,
    price_per_seat_cents: 1000,
    features: ['advanced_ai', 'custom_protocols', 'team_sharing'],
    limits: { seats: 10, projects: 50 },
  }),
});`,
              },
            ],
          },
        ],
      },
      {
        id: 'subscriptions',
        title: 'Subscriptions',
        icon: '🔄',
        description: 'Subscription lifecycle, upgrades, and cancellations.',
        sections: [
          {
            id: 'sub-lifecycle',
            title: 'Subscription Lifecycle',
            content: `A subscription links a customer to a plan with a billing period. Subscriptions move through these states:

**active** → Customer is billed, has full access to plan features
**trialing** → Free trial period, no charges yet
**paused** → Billing suspended, limited access
**cancelled** → Customer initiated cancel, access continues until period end
**expired** → Access revoked, subscription ended

State transitions happen automatically (trial → active, period end → renewal) or via API/webhook triggers (cancel, pause, upgrade).`,
          },
          {
            id: 'sub-changes',
            title: 'Upgrades & Downgrades',
            content: `When a customer changes their plan, billing handles proration automatically:

**Upgrade** — Customer is charged the prorated difference immediately. New features are available right away.
**Downgrade** — Takes effect at the end of the current billing period. Customer keeps current features until then.`,
            codeBlocks: [
              {
                language: 'typescript',
                caption: 'Change subscription plan',
                code: `// Webhook payload from your app:
{
  "event": "subscription.updated",
  "tenantId": "<ORG_UUID>",
  "data": {
    "plan_code": "enterprise",
    "seat_count": 15,
    "change_type": "upgrade"
  }
}

// Or via API:
await fetch('https://finance.rule42.ai/api/v1/orgs/<orgId>/subscriptions/<subId>', {
  method: 'PATCH',
  headers: { 'Authorization': 'Bearer <TOKEN>', 'Content-Type': 'application/json' },
  body: JSON.stringify({ plan_id: '<NEW_PLAN_UUID>' }),
});`,
              },
            ],
          },
        ],
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // USAGE & CREDITS
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'usage-credits',
    title: 'Usage & Credits',
    chapters: [
      {
        id: 'usage-billing',
        title: 'Usage-Based Billing',
        icon: '📊',
        description: 'Metering usage events and charging based on consumption.',
        sections: [
          {
            id: 'usage-concepts',
            title: 'Core Concepts',
            content: `Usage-based billing tracks what customers actually consume and charges accordingly.

**Event Type** — A billable action you define (e.g., "api_call", "file_indexed", "ai_inference"). Created in the admin console under Usage Pricing → Event Types.

**Pricing Catalog** — A named bundle that maps event types to credit costs. For example: "Standard Pricing" might price an API call at 1 credit and an AI inference at 50 credits.

**Usage Event** — A single occurrence reported by your app via the API. Each event deducts credits from the customer's balance based on the catalog price.

**Credits** — Pre-paid balance. Customers purchase credits, and usage deducts from them.`,
          },
          {
            id: 'usage-setup',
            title: 'Setup Steps',
            content: `1. **Create event types** (Admin Console → Usage Pricing → Event Types) — define what you bill for
2. **Create a pricing catalog** (Usage Pricing → Catalogs) — bundle event types with prices
3. **Add events to catalog** — set the credit cost for each event type in the catalog
4. **Assign catalog to a plan** (Plans → edit plan → select catalog)
5. **Report usage** — instrument your application to send events via API`,
          },
          {
            id: 'usage-reporting',
            title: 'Reporting Usage Events',
            content: `Send a usage event from your application whenever a billable action occurs. The billing system automatically deducts credits based on the customer's pricing catalog.`,
            codeBlocks: [
              {
                language: 'typescript',
                caption: 'Report a single usage event',
                code: `await fetch('https://finance.rule42.ai/api/v1/orgs/<orgId>/usage', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer <TOKEN>',
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    customer_id: '<CUSTOMER_UUID>',
    event_type: 'ai_inference',
    quantity: 1,
    metadata: { model: 'claude-4', tokens: 1500 },
  }),
});`,
              },
              {
                language: 'typescript',
                caption: 'Batch reporting (high-volume)',
                code: `// For high-volume apps, batch multiple events in a single request
await fetch('https://finance.rule42.ai/api/v1/orgs/<orgId>/usage/batch', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer <TOKEN>',
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    events: [
      { customer_id: '<UUID>', event_type: 'api_call', quantity: 10 },
      { customer_id: '<UUID>', event_type: 'file_indexed', quantity: 3 },
      { customer_id: '<UUID>', event_type: 'ai_inference', quantity: 1 },
    ],
  }),
});`,
              },
            ],
          },
          {
            id: 'usage-query',
            title: 'Query Usage & Balance',
            content: `Check a customer's remaining credits and usage summary.`,
            codeBlocks: [
              {
                language: 'typescript',
                caption: 'Query usage and balance',
                code: `// Check credit balance
const balance = await fetch(
  'https://finance.rule42.ai/api/v1/orgs/<orgId>/credits/balance?customerId=<UUID>',
  { headers: { 'Authorization': 'Bearer <TOKEN>' } }
).then(r => r.json());
// { balance_cents: 450000, reserved_cents: 0, available_cents: 450000 }

// Get usage summary for a period
const usage = await fetch(
  'https://finance.rule42.ai/api/v1/orgs/<orgId>/usage/summary?customerId=<UUID>&periodStart=2026-06-01&periodEnd=2026-06-30',
  { headers: { 'Authorization': 'Bearer <TOKEN>' } }
).then(r => r.json());
// [{ event_type: "ai_inference", quantity: 142, credits_consumed: 7100 }]`,
              },
            ],
          },
        ],
      },
      {
        id: 'credits',
        title: 'Credits & Balances',
        icon: '💰',
        description: 'Managing pre-paid credit balances and budget controls.',
        sections: [
          {
            id: 'credit-system',
            title: 'How Credits Work',
            content: `Credits are the billing currency. Customers pre-pay for credits, which are deducted in real-time as they use your platform.

• Each customer has a credit balance (stored in cents, e.g., 500000 = $5,000)
• Usage events deduct credits based on the pricing catalog
• Credits cannot go below zero (enforced at the database level)
• When balance drops below a configurable threshold, a \`credits.low_balance\` webhook fires
• Operations can be blocked when balance hits zero (configurable per plan)`,
          },
          {
            id: 'credit-grant',
            title: 'Granting Credits',
            content: `Top up a customer's balance manually or programmatically.`,
            codeBlocks: [
              {
                language: 'typescript',
                caption: 'Grant credits via API',
                code: `await fetch('https://finance.rule42.ai/api/v1/orgs/<orgId>/credits/grant', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer <TOKEN>',
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    customerId: '<CUSTOMER_UUID>',
    amountCents: 100000,  // $1,000 in credits
    reason: 'Monthly credit allocation',
  }),
});`,
              },
            ],
          },
          {
            id: 'credit-preauth',
            title: 'Pre-Authorization (Reservations)',
            content: `For expensive or long-running operations, reserve credits upfront to guarantee they'll be available when the operation completes.

1. **Reserve** — Call pre-authorize to hold N credits
2. **Execute** — Run the operation
3. **Settle** — Finalize with the actual cost (may be less than reserved)
4. **Release** — If operation fails, release the reservation to return credits`,
            codeBlocks: [
              {
                language: 'typescript',
                caption: 'Pre-authorize and settle',
                code: `// 1. Reserve credits before a long-running operation
const reservation = await fetch('https://finance.rule42.ai/api/v1/orgs/<orgId>/credits/pre-authorize', {
  method: 'POST',
  headers: { 'Authorization': 'Bearer <TOKEN>', 'Content-Type': 'application/json' },
  body: JSON.stringify({
    customerId: '<UUID>',
    amountCents: 5000,
    reason: 'atp_execution',
  }),
}).then(r => r.json());
// { reservation_id: "res_abc123" }

// 2. After operation completes, settle with actual cost
await fetch('https://finance.rule42.ai/api/v1/orgs/<orgId>/credits/settle', {
  method: 'POST',
  headers: { 'Authorization': 'Bearer <TOKEN>', 'Content-Type': 'application/json' },
  body: JSON.stringify({
    reservation_id: reservation.reservation_id,
    actualAmountCents: 3200,
  }),
});`,
              },
            ],
          },
        ],
      },
      {
        id: 'budgets',
        title: 'User Budgets',
        icon: '🎯',
        description: 'Per-user spending limits and budget controls.',
        sections: [
          {
            id: 'budget-overview',
            title: 'How Budgets Work',
            content: `User budgets let org admins set per-user spending limits within the organization's credit balance.

• The org-level credit balance is the hard limit
• User budgets are soft limits within that balance
• When a user exceeds their budget, their requests are blocked
• Budgets reset at the start of each billing period
• Org admins can adjust budgets at any time via the admin console or API`,
          },
          {
            id: 'budget-check',
            title: 'Check Budget Before Operations',
            content: `Query a user's remaining budget before allowing expensive operations.`,
            codeBlocks: [
              {
                language: 'typescript',
                caption: 'Check user budget',
                code: `const budget = await fetch(
  'https://finance.rule42.ai/api/v1/orgs/<orgId>/budgets/check?customerId=<ORG>&userId=<USER>',
  { headers: { 'Authorization': 'Bearer <TOKEN>' } }
).then(r => r.json());
// {
//   budget_cents: 50000,
//   spent_cents: 32000,
//   remaining_cents: 18000,
//   can_proceed: true
// }

if (!budget.can_proceed) {
  throw new Error('Budget exceeded. Contact your org admin.');
}`,
              },
            ],
          },
        ],
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // BILLING & INVOICING
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'billing',
    title: 'Billing & Invoicing',
    chapters: [
      {
        id: 'invoices',
        title: 'Invoices',
        icon: '🧾',
        description: 'Automatic invoice generation and payment tracking.',
        sections: [
          {
            id: 'invoice-generation',
            title: 'How Invoices Are Generated',
            content: `Invoices are generated automatically at the end of each billing period. They aggregate:

• **Subscription fees** — flat rate, per-seat charges
• **Usage charges** — credits consumed during the period
• **Overage charges** — usage beyond included amounts
• **Adjustments** — prorations from mid-cycle plan changes

Invoices can also be created manually for one-off charges or enterprise contract milestones.`,
          },
          {
            id: 'invoice-events',
            title: 'Invoice Webhook Events',
            content: `Subscribe to invoice events to trigger payment collection, notifications, or accounting updates.`,
            codeBlocks: [
              {
                language: 'json',
                caption: 'Invoice webhook events',
                code: `// Outbound webhook events your app can subscribe to:
{
  "invoice.created":       "New invoice generated",
  "invoice.paid":          "Payment received and recorded",
  "invoice.overdue":       "Invoice is past its due date",
  "invoice.voided":        "Invoice was cancelled/voided",
  "invoice.payment_failed": "Payment attempt failed"
}`,
              },
            ],
          },
        ],
      },
      {
        id: 'stripe-connect',
        title: 'Payment Processing (Stripe Connect)',
        icon: '💳',
        description: 'How payments work with Stripe Connect.',
        sections: [
          {
            id: 'connect-overview',
            title: 'How Stripe Connect Works',
            content: `Rule42 Billing uses Stripe Connect so each tenant organization connects their own Stripe account. This means:

• **No API keys to manage** — tenants authenticate via OAuth, not by entering secret keys
• **Platform handles routing** — payments flow through the platform's Stripe account and are automatically transferred to connected accounts
• **Revenue share** — the platform can take a configurable percentage of each transaction
• **Automatic payouts** — Stripe handles payouts to connected accounts on their configured schedule

The platform (Rule42) holds the master Stripe API key. Tenants only need to complete the Stripe Connect OAuth flow to start accepting payments.`,
          },
          {
            id: 'connect-onboarding',
            title: 'Onboarding Flow',
            content: `1. Tenant clicks "Connect Stripe Account" in Org Settings
2. Billing server generates a Stripe Connect OAuth URL
3. Tenant is redirected to Stripe to authorize the connection
4. After authorization, Stripe redirects back with the connected account ID
5. Billing stores the connected account ID and the tenant is ready to accept payments

No API keys are exchanged — the OAuth token securely links the accounts.`,
          },
        ],
      },
      {
        id: 'contracts',
        title: 'Enterprise Contracts',
        icon: '📄',
        description: 'Custom pricing, committed spend, and volume discounts.',
        sections: [
          {
            id: 'contract-features',
            title: 'Contract Features',
            content: `Enterprise contracts define custom billing terms for large customers:

• **Committed spend** — minimum annual spend guaranteed by the customer
• **Custom pricing** — override standard catalog prices for specific event types
• **Volume discounts** — tiered pricing that kicks in at high volumes
• **Payment terms** — net-30, net-60, or custom payment schedules
• **Milestone billing** — invoice at project milestones rather than monthly`,
          },
          {
            id: 'contract-api',
            title: 'Create a Contract',
            content: `Create enterprise contracts programmatically.`,
            codeBlocks: [
              {
                language: 'typescript',
                caption: 'Create an enterprise contract',
                code: `await fetch('https://finance.rule42.ai/api/v1/orgs/<orgId>/contracts', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer <TOKEN>',
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    customer_id: '<CUSTOMER_UUID>',
    committed_spend_cents: 12000000,  // $120,000/year
    discount_percent: 15,
    payment_terms: 'net-30',
    start_date: '2026-07-01',
    end_date: '2027-06-30',
  }),
});`,
              },
            ],
          },
        ],
      },
      {
        id: 'reconciliation',
        title: 'Reconciliation',
        icon: '✅',
        description: 'Matching bank transactions to billing records.',
        sections: [
          {
            id: 'recon-process',
            title: 'Reconciliation Process',
            content: `Reconciliation ensures your billing records match actual payments received:

1. **Import transactions** — Upload bank transactions via CSV or API
2. **Auto-match** — The system matches transactions to invoices by amount + reference
3. **Review unmatched** — Manually review and match any unmatched transactions
4. **Flag discrepancies** — Transactions that can't be matched are flagged for investigation

Reconciliation runs automatically for Stripe payments. Manual reconciliation is available for wire transfers, checks, and other payment methods.`,
          },
        ],
      },
      {
        id: 'revenue',
        title: 'Revenue Analytics',
        icon: '📈',
        description: 'MRR, ARR, churn, and cohort analysis.',
        sections: [
          {
            id: 'revenue-metrics',
            title: 'Available Metrics',
            content: `The revenue dashboard tracks key SaaS financial metrics:

• **MRR** (Monthly Recurring Revenue) — sum of all active monthly subscription fees
• **ARR** (Annual Recurring Revenue) — MRR × 12
• **Churn Rate** — percentage of customers who cancelled in a period
• **Net Revenue Retention** — revenue from existing customers including expansion
• **Cohort Retention** — how long customers stay, grouped by signup month
• **ARPU** (Average Revenue Per User) — total revenue / active customers`,
          },
          {
            id: 'revenue-api',
            title: 'Query Metrics via API',
            content: `Access revenue metrics programmatically for custom dashboards or reporting.`,
            codeBlocks: [
              {
                language: 'typescript',
                caption: 'Revenue API endpoints',
                code: `// Monthly Recurring Revenue
GET /api/v1/platform/reports/mrr

// Annual Recurring Revenue
GET /api/v1/platform/reports/arr

// Churn rate for the last 30 days
GET /api/v1/platform/reports/churn?period=30d

// Cohort analysis
GET /api/v1/platform/reports/cohorts`,
              },
            ],
          },
        ],
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // INTEGRATIONS
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'integrations',
    title: 'Integrations',
    chapters: [
      {
        id: 'webhooks',
        title: 'Webhooks & Events',
        icon: '🔗',
        description: 'Bi-directional event system for real-time notifications.',
        sections: [
          {
            id: 'webhook-inbound',
            title: 'Inbound Webhooks (Your App → Billing)',
            content: `Inbound webhooks let your external services (auth, CRM, etc.) notify billing of changes.

The auth webhook endpoint accepts HMAC-signed payloads:
• **URL:** POST /webhooks/auth
• **Signature:** X-Auth-Signature header (HMAC-SHA256 of the request body)
• **Events:** subscription.created, subscription.updated, member.added, member.removed`,
            codeBlocks: [
              {
                language: 'typescript',
                caption: 'Send an inbound webhook',
                code: `import crypto from 'crypto';

const body = JSON.stringify({
  eventId: 'evt_' + crypto.randomUUID(),
  event: 'subscription.created',
  tenantId: '<ORG_UUID>',
  data: { org_name: 'Acme', plan_code: 'pro', seat_count: 5 },
});

const sig = crypto.createHmac('sha256', WEBHOOK_SECRET)
  .update(body).digest('hex');

await fetch('https://finance.rule42.ai/webhooks/auth', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'X-Auth-Signature': sig,
  },
  body,
});`,
              },
            ],
          },
          {
            id: 'webhook-outbound',
            title: 'Outbound Webhooks (Billing → Your App)',
            content: `Outbound webhooks notify YOUR applications when billing events happen. Register endpoints in the admin console under Org Settings → Webhooks.

**Available event categories:**
• \`customer.*\` — customer created, updated, deleted
• \`subscription.*\` — subscription lifecycle changes
• \`credits.*\` — balance changes, low balance alerts
• \`usage.*\` — usage thresholds, budget exceeded
• \`invoice.*\` — invoice generated, paid, overdue
• \`plan.*\` — plan changes, permission updates

Each endpoint receives HMAC-signed POST requests with a JSON payload.`,
          },
          {
            id: 'webhook-payload',
            title: 'Webhook Payload Format',
            content: `All outbound webhook payloads follow a consistent structure.`,
            codeBlocks: [
              {
                language: 'json',
                caption: 'Webhook payload structure',
                code: `{
  "id": "evt_abc123",
  "type": "credits.low_balance",
  "created_at": "2026-06-20T10:30:00Z",
  "data": {
    "customer_id": "<UUID>",
    "customer_name": "Acme Corp",
    "balance_cents": 500,
    "threshold_cents": 1000
  }
}`,
              },
            ],
          },
          {
            id: 'webhook-verify',
            title: 'Verifying Webhook Signatures',
            content: `Always verify the HMAC signature before processing webhook payloads. This prevents spoofed requests.`,
            codeBlocks: [
              {
                language: 'typescript',
                caption: 'Verify webhook signature',
                code: `import crypto from 'crypto';

function verifyWebhook(rawBody: string, signature: string, secret: string): boolean {
  const expected = crypto
    .createHmac('sha256', secret)
    .update(rawBody)
    .digest('hex');
  return crypto.timingSafeEqual(
    Buffer.from(signature),
    Buffer.from(expected)
  );
}

// In your webhook handler:
app.post('/webhooks/billing', (req, res) => {
  const sig = req.headers['x-webhook-signature'] as string;
  if (!verifyWebhook(req.rawBody, sig, WEBHOOK_SECRET)) {
    return res.status(401).json({ error: 'Invalid signature' });
  }
  // Process the event...
});`,
              },
            ],
          },
        ],
      },
      {
        id: 'sdk-integration',
        title: 'SDK & Client Libraries',
        icon: '📦',
        description: 'Using the billing SDK in your application.',
        sections: [
          {
            id: 'sdk-install',
            title: 'Installation',
            content: `Install the Rule42 Billing SDK for your stack.`,
            codeBlocks: [
              {
                language: 'bash',
                caption: 'Node.js / TypeScript',
                code: `pnpm add @rule42/billing-sdk
# or
npm install @rule42/billing-sdk`,
              },
            ],
          },
          {
            id: 'sdk-init',
            title: 'Client Initialization',
            content: `Create a billing client instance for server-side use. Never expose the API key in client-side code.`,
            codeBlocks: [
              {
                language: 'typescript',
                caption: 'Initialize the SDK',
                code: `import { BillingClient } from '@rule42/billing-sdk';

const billing = new BillingClient({
  apiKey: process.env.BILLING_API_KEY!,
  baseUrl: 'https://finance.rule42.ai/api/v1',
  orgId: process.env.BILLING_ORG_ID!,
});

// Create a customer
const customer = await billing.customers.create({
  email: 'user@example.com',
  name: 'Jane Doe',
});

// Report usage
await billing.usage.report({
  customerId: customer.id,
  metricKey: 'api_calls',
  quantity: 1,
});

// Check subscription status
const sub = await billing.subscriptions.get(customer.id);
console.log(sub.status); // 'active'`,
              },
            ],
          },
          {
            id: 'sdk-methods',
            title: 'SDK Method Reference',
            content: `**Customers**
• \`billing.customers.create(params)\` — Create a new customer
• \`billing.customers.list(params?)\` — List with pagination and filtering
• \`billing.customers.get(id)\` — Get a single customer
• \`billing.customers.update(id, params)\` — Update customer fields

**Subscriptions**
• \`billing.subscriptions.create(params)\` — Subscribe a customer to a plan
• \`billing.subscriptions.get(customerId)\` — Get current subscription
• \`billing.subscriptions.cancel(subscriptionId)\` — Cancel a subscription

**Usage**
• \`billing.usage.report(params)\` — Submit a metered usage event
• \`billing.usage.reportBatch(events)\` — Submit multiple events
• \`billing.usage.summary(customerId, period)\` — Get aggregated usage

**Credits**
• \`billing.credits.grant(params)\` — Add credits to a balance
• \`billing.credits.balance(customerId)\` — Get current balance
• \`billing.credits.preAuthorize(params)\` — Reserve credits
• \`billing.credits.settle(reservationId, actualAmount)\` — Finalize reservation

**Invoices**
• \`billing.invoices.list(customerId)\` — List invoices
• \`billing.invoices.get(invoiceId)\` — Get invoice details
• \`billing.invoices.downloadPdf(invoiceId)\` — Get PDF download URL

**Checkout & Portal**
• \`billing.checkout.create(params)\` — Create hosted checkout URL
• \`billing.portal.create(params)\` — Create customer portal URL

**Webhooks**
• \`billing.webhooks.verify(body, signature)\` — Verify and parse a webhook`,
          },
        ],
      },
      {
        id: 'mcp-tools',
        title: 'MCP Server (AI Agents)',
        icon: '🤖',
        description: 'Integrate billing into AI agent workflows via MCP.',
        sections: [
          {
            id: 'mcp-setup',
            title: 'MCP Configuration',
            content: `The Rule42 Billing MCP server exposes billing operations as tools that AI agents can use directly. Add it to your MCP configuration to let agents manage billing, check balances, and provision customers.`,
            codeBlocks: [
              {
                language: 'json',
                caption: 'MCP client configuration',
                code: `{
  "mcpServers": {
    "billing": {
      "command": "npx",
      "args": ["@rule42/billing-mcp"],
      "env": {
        "BILLING_API_URL": "https://finance.rule42.ai",
        "MCP_AUTH_TOKEN": "<your-jwt-token>"
      }
    }
  }
}`,
              },
            ],
          },
          {
            id: 'mcp-tools-list',
            title: 'Available MCP Tools',
            content: `The MCP server exposes these tools:

| Tool | Description |
|------|-------------|
| \`billing_provision_tenant\` | Create a new tenant with full zero-touch provisioning |
| \`billing_create_plan\` | Create a pricing plan |
| \`billing_create_customer\` | Create a customer |
| \`billing_create_subscription\` | Subscribe a customer to a plan |
| \`billing_report_usage\` | Submit a metered usage event |
| \`billing_grant_credits\` | Add credits to a customer |
| \`billing_check_subscription\` | Get current subscription status |
| \`billing_get_invoice\` | Retrieve invoice details |
| \`billing_list_customers\` | List customers with filtering |
| \`billing_configure_byod\` | Set up external database |
| \`billing_create_webhook\` | Register a webhook endpoint |
| \`billing_get_integration_guide\` | Get tailored setup instructions |
| \`billing_update_email_template\` | Customize an email template |
| \`billing_get_usage_summary\` | Usage summary for a customer |
| \`billing_update_plan\` | Update an existing plan |`,
          },
        ],
      },
      {
        id: 'email-templates',
        title: 'Email Templates',
        icon: '✉️',
        description: 'Customizing transactional email templates.',
        sections: [
          {
            id: 'email-overview',
            title: 'Email System',
            content: `Rule42 Billing sends transactional emails for key billing events. Each template can be customized per-tenant from the admin console or via API.

**Email categories:**
• **Invoicing** — Invoice generated, payment received, payment failed, payment reminder
• **Subscription** — Welcome email, plan changed, trial ending, cancellation confirmation
• **Credits** — Balance low alert, credits purchased, credits granted
• **Admin** — New customer notification, churn alert

Templates use Handlebars syntax for variable interpolation. Each template has a subject line, HTML body, and plain-text fallback.`,
          },
          {
            id: 'email-customize',
            title: 'Customizing Templates',
            content: `Customize templates via the admin console (Email Templates page) or API.`,
            codeBlocks: [
              {
                language: 'typescript',
                caption: 'Update a template via API',
                code: `await fetch('https://finance.rule42.ai/api/v1/orgs/<orgId>/email-templates/invoice_created', {
  method: 'PUT',
  headers: {
    'Authorization': 'Bearer <TOKEN>',
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    subject: 'Your invoice from {{company_name}} - {{invoice_number}}',
    html: '<h1>Invoice #{{invoice_number}}</h1><p>Amount: {{amount}}</p>...',
  }),
});`,
              },
            ],
          },
        ],
      },
      {
        id: 'access-enforcement',
        title: 'Access Enforcement & Provisioning',
        icon: '🛡️',
        description: 'Complete guide to enforcing billing status in your application — covers every integration pattern, auth system, and scenario.',
        sections: [
          {
            id: 'enforcement-overview',
            title: 'Enforcement Architecture',
            content: `Rule42 Billing gives you four ways to enforce billing status in your application. You can use one pattern or combine them.

**Pattern 1 — Sync from Auth (rule42-auth native)**
Billing syncs bi-directionally with rule42-auth. Business roles in auth become entitlements in billing. When a subscription changes, billing tells auth to provision/deprovision roles automatically.
• Best for: Teams already using rule42-auth for identity management.
• Setup: Plans → Entitlement Mapping → "Sync from Auth" mode.
• Effort: Zero-code — just configure the mapping in the admin UI.

**Pattern 2 — Outbound Webhooks (Push)**
Billing pushes HTTP POST events to your configured endpoint whenever a subscription, payment, or entitlement event occurs. Your system receives the event and updates permissions in your auth system.
• Best for: Any auth system (Auth0, Okta, Clerk, Firebase Auth, Keycloak, custom).
• Setup: Plans → Entitlement Mapping → "Outbound Webhook" mode, then Webhooks & Sync page.
• Effort: You write a webhook receiver endpoint (Express, Next.js, Flask, etc.).

**Pattern 3 — API Polling (Pull)**
Your application calls the billing API to check a customer's entitlement status before granting access. No webhook setup needed — just an API call.
• Best for: Serverless functions, simple apps, feature gates, client-side checks.
• Setup: Plans → Entitlement Mapping → "API Polling" mode + map entitlements.
• Effort: Add middleware or React hooks that call the billing API.

**Pattern 4 — Manual / Automation Rules**
Billing tracks entitlements but does not automatically enforce them. Automation rules send notifications (email, Slack, external webhook) when events occur, and you handle access changes manually.
• Best for: Early-stage products, low-volume, or compliance-heavy workflows that require human approval.
• Setup: Plans → Entitlement Mapping → "Manual" mode + configure Automation Rules.
• Effort: Set up notification channels, handle access changes in your ops workflow.

**Hybrid approach:** Most production systems combine patterns. Use webhooks for immediate provisioning, API polling for real-time feature gates, and automation rules for dunning workflows.`,
          },
          {
            id: 'enforcement-scenarios',
            title: 'Scenario Guide — What to Set Up',
            content: `Choose your scenario below to see exactly what to configure.

**Scenario 1: "I use rule42-auth and want automatic provisioning"**
→ Use Sync from Auth mode
1. Go to Plans → click your plan → Features & Entitlements
2. Select "Sync from Auth" enforcement mode
3. Click "Sync from Auth" button to pull business roles into the catalog
4. Check the entitlements that should be granted with this plan (e.g., Pro plan → check "pro" and "community" entitlements)
5. Click "Save Entitlement Mapping"
6. Done — when a user subscribes to this plan, billing tells rule42-auth to assign the business roles. When they cancel, roles are revoked.

**Scenario 2: "I use Auth0/Okta/Clerk and want real-time access changes"**
→ Use Outbound Webhook mode
1. Go to Plans → click your plan → Features & Entitlements
2. Select "Outbound Webhook" enforcement mode
3. Note the events that will fire (subscription.created, canceled, etc.)
4. Build a webhook receiver in your backend (see code examples in the UI)
5. Register the endpoint in Webhooks & Sync page
6. In your webhook handler, call your auth system's API to update roles/groups
7. Create a test subscription to verify the webhook fires and permissions change

**Scenario 3: "I want to gate features without any webhook setup"**
→ Use API Polling mode
1. Go to Plans → click your plan → Features & Entitlements
2. Select "API Polling" enforcement mode
3. Map entitlements to the plan (check the entitlements this plan should grant)
4. Add an API check in your backend before granting access to premium features
5. Optionally add a React hook for client-side feature gating (UX only — always enforce server-side too)
6. Cache responses for 60–300 seconds for performance

**Scenario 4: "I want to be notified but handle access manually"**
→ Use Manual mode + Automation Rules
1. Go to Plans → click your plan → Features & Entitlements
2. Select "Manual / None" enforcement mode
3. Optionally map entitlements for record-keeping
4. Go to Operations → Automation Rules
5. Create rules: subscription.created → send_email, payment.failed → send_email (3-day delay), etc.
6. When you receive notifications, manually update access in your system

**Scenario 5: "I want dunning automation (payment reminders, suspension)"**
→ Use Automation Rules (works with any enforcement mode)
1. Go to Operations → Automation Rules
2. Create these rules:
   • invoice.past_due → send_email "Payment Reminder" (0-day delay)
   • invoice.past_due → send_email "Final Warning" (7-day delay)
   • invoice.past_due → revoke_entitlements OR send_webhook (14-day delay)
   • customer.reinstated → grant_entitlements OR send_webhook (0-day delay)
3. Attach rules to specific plans if needed (e.g., stricter dunning for enterprise plans)

**Scenario 6: "I want to send events to Slack/Discord when billing events happen"**
→ Use Automation Rules with notify_external
1. Go to Operations → Automation Rules
2. Create rules with action type "Notify External"
3. Set the webhook URL to your Slack incoming webhook or Discord webhook
4. Configure conditions (e.g., only for payment.failed events, or only for enterprise plans)

**Scenario 7: "I want plan upgrades/downgrades to change permissions"**
→ Use Outbound Webhooks or Auth Sync
1. The events subscription.upgraded and subscription.downgraded fire automatically
2. In webhook mode: your handler receives the old and new plan tier, update permissions accordingly
3. In auth sync mode: billing automatically remaps entitlements based on the new plan's mapping
4. In API polling mode: the next entitlement check will return the new plan's entitlements (eventual consistency)`,
          },
          {
            id: 'enforcement-push',
            title: 'Push Pattern — Webhook Integration',
            content: `Register a webhook endpoint in the admin console (Webhooks & Sync page) to receive billing events. Your endpoint processes the event and updates access in your auth system.

**Complete event reference:**

| Event | When it fires | What to do |
|-------|---------------|------------|
| subscription.created | New subscription activated | Grant access / assign roles |
| subscription.canceled | User or system cancels | Revoke access / remove roles |
| subscription.expired | Subscription period ended without renewal | Revoke access |
| subscription.upgraded | Plan changed to higher tier | Upgrade permissions |
| subscription.downgraded | Plan changed to lower tier | Downgrade permissions |
| subscription.trial_ending | Trial period ending (configurable days before) | Send upgrade prompt |
| invoice.past_due | Invoice not paid by due date | Send payment reminder |
| payment.failed | Payment attempt failed | Warn user, start dunning |
| payment.succeeded | Payment collected successfully | Restore access if restricted |
| customer.reinstated | Previously suspended customer restored | Re-grant access |
| customer.suspended | Customer suspended (manual or automatic) | Revoke all access |
| entitlements.granted | Specific entitlements activated | Sync specific permissions |
| entitlements.revoked | Specific entitlements removed | Remove specific permissions |

**Webhook payload format:**
\`\`\`json
{
  "event": "subscription.created",
  "timestamp": "2026-06-20T14:30:00Z",
  "data": {
    "subscriptionId": "sub_abc123",
    "customerId": "cus_xyz789",
    "planId": "plan_pro",
    "planTier": "pro",
    "status": "active",
    "entitlements": ["pro-access", "cloud-sync", "analytics"]
  }
}
\`\`\`

**Security:** All webhooks include an \`X-Billing-Signature\` header containing an HMAC-SHA256 hash of the payload. Always verify this signature before processing.

**Retry policy:** Failed deliveries (non-2xx response) are retried 3 times with exponential backoff (1min, 5min, 30min).

**Idempotency:** Events include a unique \`eventId\`. Store processed event IDs and skip duplicates to handle retries safely.`,
            codeBlocks: [
              {
                language: 'typescript',
                caption: 'Complete webhook receiver with signature verification',
                code: `import crypto from 'crypto';
import express from 'express';

const WEBHOOK_SECRET = process.env.BILLING_WEBHOOK_SECRET!;

// Track processed events for idempotency
const processedEvents = new Set<string>();

app.post('/webhooks/billing', express.json(), async (req, res) => {
  // 1. Verify HMAC-SHA256 signature
  const signature = req.headers['x-billing-signature'] as string;
  const expected = crypto
    .createHmac('sha256', WEBHOOK_SECRET)
    .update(JSON.stringify(req.body))
    .digest('hex');

  if (signature !== expected) {
    console.error('Invalid webhook signature');
    return res.status(401).json({ error: 'Invalid signature' });
  }

  // 2. Idempotency check
  const { eventId, event, data } = req.body;
  if (processedEvents.has(eventId)) {
    return res.json({ received: true, skipped: true });
  }
  processedEvents.add(eventId);

  // 3. Process the event
  try {
    switch (event) {
      case 'subscription.created':
        await grantAccess(data.customerId, data.planTier, data.entitlements);
        break;

      case 'subscription.canceled':
      case 'subscription.expired':
        await revokeAccess(data.customerId);
        break;

      case 'subscription.upgraded':
        await upgradeAccess(data.customerId, data.planTier, data.entitlements);
        break;

      case 'subscription.downgraded':
        await downgradeAccess(data.customerId, data.planTier, data.entitlements);
        break;

      case 'payment.failed':
        await handlePaymentFailure(data.customerId, data.retryCount);
        break;

      case 'payment.succeeded':
        await restoreFullAccess(data.customerId);
        break;

      case 'customer.suspended':
        await suspendAllAccess(data.customerId);
        break;

      case 'customer.reinstated':
        await reinstateAccess(data.customerId, data.planTier);
        break;

      default:
        console.log('Unhandled billing event:', event);
    }
  } catch (err) {
    console.error('Webhook processing error:', err);
    return res.status(500).json({ error: 'Processing failed' });
  }

  res.json({ received: true });
});`,
              },
            ],
          },
          {
            id: 'enforcement-push-auth0',
            title: 'Webhook Integration — Auth0',
            content: `Step-by-step guide for integrating billing webhooks with Auth0.

**Architecture:**
Billing → Webhook → Your backend → Auth0 Management API → User roles/metadata updated

**Setup steps:**

1. **Create an Auth0 Machine-to-Machine application** for your backend to call the Auth0 Management API:
   - Auth0 Dashboard → Applications → Create → Machine to Machine
   - Authorize the Auth0 Management API with scopes: \`read:users\`, \`update:users\`, \`read:roles\`, \`create:role_members\`, \`delete:role_members\`

2. **Create Auth0 roles** that map to your plan tiers:
   - Auth0 Dashboard → User Management → Roles
   - Create roles: \`billing:free\`, \`billing:pro\`, \`billing:business\`, \`billing:enterprise\`

3. **Build your webhook receiver** (see code example below)

4. **Register the webhook** in billing admin:
   - Go to Webhooks & Sync → Add Endpoint
   - URL: \`https://api.yourapp.com/webhooks/billing\`
   - Events: subscription.created, subscription.canceled, subscription.upgraded, subscription.downgraded, payment.failed

5. **Map customer IDs**: Ensure your billing customer records include the Auth0 user ID. Set this when creating the customer via the billing API:
   \`POST /api/v1/orgs/{orgId}/customers\` with \`{ authUserId: "auth0|abc123" }\`

6. **Test**: Create a test subscription and verify the Auth0 user's roles update.`,
            codeBlocks: [
              {
                language: 'typescript',
                caption: 'Auth0 webhook receiver — full implementation',
                code: `import { ManagementClient } from 'auth0';

const auth0 = new ManagementClient({
  domain: process.env.AUTH0_DOMAIN!,
  clientId: process.env.AUTH0_M2M_CLIENT_ID!,
  clientSecret: process.env.AUTH0_M2M_CLIENT_SECRET!,
});

// Map billing plan tiers to Auth0 role IDs
const PLAN_ROLE_MAP: Record<string, string> = {
  free: 'rol_free_abc',
  pro: 'rol_pro_def',
  business: 'rol_biz_ghi',
  enterprise: 'rol_ent_jkl',
};

// All premium roles (for bulk removal on cancel)
const PREMIUM_ROLES = ['rol_pro_def', 'rol_biz_ghi', 'rol_ent_jkl'];

app.post('/webhooks/billing', express.json(), async (req, res) => {
  // ... signature verification (see previous example) ...
  const { event, data } = req.body;

  switch (event) {
    case 'subscription.created':
    case 'subscription.upgraded': {
      const roleId = PLAN_ROLE_MAP[data.planTier];
      if (!roleId) break;

      // Remove all premium roles first, then assign the new one
      for (const oldRole of PREMIUM_ROLES) {
        await auth0.roles.removeUsers({ id: oldRole },
          { users: [data.authUserId] }).catch(() => {});
      }
      await auth0.roles.assignUsers({ id: roleId },
        { users: [data.authUserId] });

      // Update app_metadata with plan info
      await auth0.users.update({ id: data.authUserId }, {
        app_metadata: {
          billing_plan: data.planTier,
          billing_active: true,
          billing_customer_id: data.customerId,
          entitlements: data.entitlements,
        },
      });
      break;
    }

    case 'subscription.canceled':
    case 'subscription.expired': {
      // Remove all premium roles
      for (const roleId of PREMIUM_ROLES) {
        await auth0.roles.removeUsers({ id: roleId },
          { users: [data.authUserId] }).catch(() => {});
      }
      // Assign free role
      await auth0.roles.assignUsers(
        { id: PLAN_ROLE_MAP.free! },
        { users: [data.authUserId] }
      );
      await auth0.users.update({ id: data.authUserId }, {
        app_metadata: {
          billing_plan: 'free',
          billing_active: false,
          entitlements: [],
        },
      });
      break;
    }

    case 'payment.failed':
      await auth0.users.update({ id: data.authUserId }, {
        app_metadata: { billing_payment_failed: true },
      });
      break;

    case 'payment.succeeded':
      await auth0.users.update({ id: data.authUserId }, {
        app_metadata: { billing_payment_failed: false },
      });
      break;
  }

  res.json({ received: true });
});`,
              },
            ],
          },
          {
            id: 'enforcement-push-okta',
            title: 'Webhook Integration — Okta',
            content: `Step-by-step guide for integrating billing webhooks with Okta.

**Architecture:**
Billing → Webhook → Your backend → Okta Admin API → User groups/profile updated

**Setup steps:**

1. **Create an Okta API token** or OAuth service app:
   - Okta Admin Console → Security → API → Tokens → Create Token
   - Or: Applications → Create → API Services (for OAuth2 client credentials)

2. **Create Okta groups** that map to your plan tiers:
   - Directory → Groups → Add Group
   - Create groups: \`billing-free\`, \`billing-pro\`, \`billing-business\`, \`billing-enterprise\`

3. **Set up group-based access policies**: In your Okta apps, create authorization policies that grant scopes/claims based on group membership.

4. **Build your webhook receiver** (see code below)

5. **Register the webhook** in billing admin:
   - Webhooks & Sync → Add Endpoint
   - URL: \`https://api.yourapp.com/webhooks/billing\`

6. **Customer ID mapping**: Store the Okta user ID on billing customer records.

7. **Test**: Create a subscription and verify the Okta user is added to the correct group.

**Okta Inline Hooks (Alternative):**
Instead of outbound webhooks, you can use Okta Inline Hooks to call the billing API during token issuance. This adds entitlement claims to the access token in real-time. See the API Polling section for the API call format.`,
            codeBlocks: [
              {
                language: 'typescript',
                caption: 'Okta webhook receiver — group-based provisioning',
                code: `import { Client } from '@okta/okta-sdk-nodejs';

const oktaClient = new Client({
  orgUrl: process.env.OKTA_ORG_URL!,
  token: process.env.OKTA_API_TOKEN!,
});

const PLAN_GROUP_MAP: Record<string, string> = {
  free: 'grp_free_id',
  pro: 'grp_pro_id',
  business: 'grp_biz_id',
  enterprise: 'grp_ent_id',
};

const PREMIUM_GROUPS = ['grp_pro_id', 'grp_biz_id', 'grp_ent_id'];

app.post('/webhooks/billing', express.json(), async (req, res) => {
  const { event, data } = req.body;

  switch (event) {
    case 'subscription.created':
    case 'subscription.upgraded': {
      const groupId = PLAN_GROUP_MAP[data.planTier];
      if (!groupId) break;

      // Remove from all premium groups
      for (const gid of PREMIUM_GROUPS) {
        await oktaClient.removeUserFromGroup(gid, data.authUserId)
          .catch(() => {});
      }
      // Add to new group
      await oktaClient.addUserToGroup(groupId, data.authUserId);

      // Update user profile with billing info
      await oktaClient.partialUpdateUser(data.authUserId, {
        profile: {
          billingPlan: data.planTier,
          billingActive: true,
          billingEntitlements: data.entitlements.join(','),
        },
      });
      break;
    }

    case 'subscription.canceled':
    case 'subscription.expired': {
      for (const gid of PREMIUM_GROUPS) {
        await oktaClient.removeUserFromGroup(gid, data.authUserId)
          .catch(() => {});
      }
      await oktaClient.addUserToGroup(PLAN_GROUP_MAP.free!, data.authUserId);
      await oktaClient.partialUpdateUser(data.authUserId, {
        profile: { billingPlan: 'free', billingActive: false },
      });
      break;
    }
  }

  res.json({ received: true });
});`,
              },
            ],
          },
          {
            id: 'enforcement-push-custom',
            title: 'Webhook Integration — Custom Auth / Database',
            content: `If you manage permissions in your own database or a custom auth system, use billing webhooks to keep permissions in sync.

**Architecture:**
Billing → Webhook → Your backend → Your database / permission system

**Common patterns:**

**A. Permissions stored in your database (PostgreSQL, MySQL, etc.)**
When a billing event arrives, update the user's plan/role column and feature flags in your database. Your application reads these values to determine access.

**B. Permissions stored in Redis / cache**
For high-performance feature gating, store entitlements in Redis. Webhooks update Redis, and your API reads from Redis on every request. Set a TTL as a safety net.

**C. JWT claims enrichment**
When a billing event arrives, update a "plan" claim in your user record. Next time the user's JWT is issued (login refresh), it includes the updated plan. For immediate effect, combine with API polling.

**D. External service notification (Slack, PagerDuty)**
Use automation rules with "Notify External" action to post billing events to Slack channels, PagerDuty, or any HTTP endpoint for ops visibility.`,
            codeBlocks: [
              {
                language: 'typescript',
                caption: 'Database-backed permission update',
                code: `// PostgreSQL example — update user permissions from billing webhook
import { pool } from './db';

app.post('/webhooks/billing', express.json(), async (req, res) => {
  const { event, data } = req.body;

  switch (event) {
    case 'subscription.created':
    case 'subscription.upgraded':
      await pool.query(
        \`UPDATE users
         SET plan_tier = $1,
             features = $2,
             billing_active = true,
             updated_at = NOW()
         WHERE billing_customer_id = $3\`,
        [data.planTier, JSON.stringify(data.entitlements), data.customerId]
      );
      // Invalidate cache
      await redis.del(\`user:features:\${data.customerId}\`);
      break;

    case 'subscription.canceled':
    case 'subscription.expired':
      await pool.query(
        \`UPDATE users
         SET plan_tier = 'free',
             features = '[]',
             billing_active = false,
             updated_at = NOW()
         WHERE billing_customer_id = $1\`,
        [data.customerId]
      );
      await redis.del(\`user:features:\${data.customerId}\`);
      break;

    case 'payment.failed':
      await pool.query(
        \`UPDATE users
         SET payment_status = 'failed',
             grace_period_end = NOW() + INTERVAL '14 days'
         WHERE billing_customer_id = $1\`,
        [data.customerId]
      );
      break;
  }

  res.json({ received: true });
});

// Middleware: check features from DB/cache
async function requireFeature(feature: string) {
  return async (req, res, next) => {
    const cacheKey = \`user:features:\${req.user.billingId}\`;
    let features = await redis.get(cacheKey);
    if (!features) {
      const row = await pool.query(
        'SELECT features FROM users WHERE billing_customer_id = $1',
        [req.user.billingId]
      );
      features = row.rows[0]?.features || '[]';
      await redis.setex(cacheKey, 300, features); // cache 5 min
    }
    const list = JSON.parse(features);
    if (!list.includes(feature)) {
      return res.status(403).json({ error: 'Upgrade required' });
    }
    next();
  };
}`,
              },
            ],
          },
          {
            id: 'enforcement-pull',
            title: 'Pull Pattern — API Entitlement Check',
            content: `Call the entitlement check API from your application before granting access to a feature. This works with any auth system — no webhook setup required.

**Two endpoints available:**

**Quick check** — Does this customer have a specific entitlement?
\`GET /api/v1/orgs/{orgId}/entitlements/check?customerId={id}&slug={slug}\`
Response: \`{ "entitled": true }\` or \`{ "entitled": false }\`

**Full list** — What entitlements does this customer have?
\`GET /api/v1/orgs/{orgId}/entitlements/customer/{customerId}\`
Response: \`{ "entitled": true, "entitlements": [{ "slug": "pro-access", "displayName": "Pro Access", "status": "active" }] }\`

**Authentication:** All API calls require a Bearer token. Use a service token (BILLING_SERVICE_TOKEN) or the user's JWT.

**Performance best practices:**
• Cache responses for 60–300 seconds (entitlement changes are infrequent)
• Use HTTP cache headers — the API sets appropriate ETags
• For high-volume services, consider also adding webhooks for cache invalidation
• The quick-check endpoint is optimized for single-feature gates

**When to use API Polling vs. Webhooks:**
• API polling = your app asks "is this user entitled?" on each request
• Webhooks = billing tells your app "this user's entitlements changed"
• Best practice: use both — webhooks for instant provisioning, API for verification`,
            codeBlocks: [
              {
                language: 'typescript',
                caption: 'Express middleware — gate routes behind entitlements',
                code: `const BILLING_API = process.env.BILLING_API_URL;
const SERVICE_TOKEN = process.env.BILLING_SERVICE_TOKEN;
const ORG_ID = process.env.BILLING_ORG_ID;

// Simple check — for individual feature gates
async function requireEntitlement(slug: string) {
  return async (req: Request, res: Response, next: NextFunction) => {
    const customerId = req.user.billingCustomerId;
    try {
      const response = await fetch(
        \`\${BILLING_API}/api/v1/orgs/\${ORG_ID}/entitlements/check\` +
        \`?customerId=\${customerId}&slug=\${slug}\`,
        {
          headers: { Authorization: \`Bearer \${SERVICE_TOKEN}\` },
          signal: AbortSignal.timeout(3000), // 3s timeout
        }
      );
      const { entitled } = await response.json();
      if (!entitled) {
        return res.status(403).json({
          error: 'Feature not available on your current plan',
          required_entitlement: slug,
          upgrade_url: '/pricing',
        });
      }
      next();
    } catch (err) {
      // Fail open or closed depending on your policy
      console.error('Entitlement check failed:', err);
      next(); // fail open — or res.status(503) to fail closed
    }
  };
}

// Usage — protect routes
app.get('/api/cloud-sync', requireEntitlement('cloud-sync'), syncHandler);
app.get('/api/analytics', requireEntitlement('analytics'), analyticsHandler);
app.post('/api/bulk-export', requireEntitlement('bulk-export'), exportHandler);`,
              },
              {
                language: 'typescript',
                caption: 'React hook — client-side feature gating',
                code: `import { useQuery } from '@tanstack/react-query';

function useEntitlement(slug: string) {
  const { data, isLoading } = useQuery({
    queryKey: ['entitlement', slug],
    queryFn: async () => {
      const res = await fetch(\`/api/billing/entitlements/check?slug=\${slug}\`);
      if (!res.ok) return { entitled: false };
      return res.json() as Promise<{ entitled: boolean }>;
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 1,
  });

  return { entitled: data?.entitled ?? false, loading: isLoading };
}

// Full entitlements list — for dashboards showing all features
function useAllEntitlements() {
  const { data, isLoading } = useQuery({
    queryKey: ['entitlements', 'all'],
    queryFn: async () => {
      const res = await fetch('/api/billing/entitlements/mine');
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
  });
  return { entitlements: data?.entitlements ?? [], loading: isLoading };
}

// Feature gate component
function FeatureGate({ slug, children, fallback }: {
  slug: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}) {
  const { entitled, loading } = useEntitlement(slug);
  if (loading) return <Skeleton />;
  if (!entitled) return fallback ?? <UpgradePrompt feature={slug} />;
  return <>{children}</>;
}

// Usage
function Dashboard() {
  return (
    <div>
      <BasicStats />
      <FeatureGate slug="analytics">
        <AdvancedAnalytics />
      </FeatureGate>
      <FeatureGate slug="cloud-sync" fallback={<CloudSyncUpsell />}>
        <CloudSyncWidget />
      </FeatureGate>
    </div>
  );
}`,
              },
              {
                language: 'typescript',
                caption: 'Next.js App Router — server-side entitlement check',
                code: `// lib/billing.ts — reusable entitlement checker
export async function checkEntitlement(
  customerId: string,
  slug: string
): Promise<boolean> {
  const res = await fetch(
    \`\${process.env.BILLING_API}/api/v1/orgs/\${process.env.BILLING_ORG_ID}\` +
    \`/entitlements/check?customerId=\${customerId}&slug=\${slug}\`,
    {
      headers: { Authorization: \`Bearer \${process.env.BILLING_TOKEN}\` },
      next: { revalidate: 300 }, // ISR cache for 5 min
    }
  );
  if (!res.ok) return false;
  const data = await res.json();
  return data.entitled === true;
}

// app/dashboard/analytics/page.tsx — RSC with entitlement gate
import { getServerSession } from 'next-auth';
import { checkEntitlement } from '@/lib/billing';
import { redirect } from 'next/navigation';

export default async function AnalyticsPage() {
  const session = await getServerSession();
  const entitled = await checkEntitlement(
    session!.user.billingCustomerId,
    'analytics'
  );

  if (!entitled) {
    redirect('/pricing?feature=analytics');
  }

  return <AnalyticsDashboard />;
}

// middleware.ts — protect API routes at the edge
import { NextResponse } from 'next/server';

export async function middleware(request: NextRequest) {
  if (request.nextUrl.pathname.startsWith('/api/premium')) {
    const session = await getToken({ req: request });
    if (!session) return NextResponse.redirect('/login');

    const entitled = await checkEntitlement(
      session.billingCustomerId as string,
      'premium-api'
    );
    if (!entitled) {
      return NextResponse.json(
        { error: 'Premium plan required' },
        { status: 403 }
      );
    }
  }
}`,
              },
            ],
          },
          {
            id: 'enforcement-automation',
            title: 'Automation Rules — Trigger → Action',
            content: `Configure automation rules in the billing admin to execute actions automatically when billing events occur. Rules are defined as trigger → condition → action chains.

**Built-in trigger types:**
• **subscription.created** — New subscription activated
• **subscription.canceled** — Subscription cancelled by user or system
• **subscription.expired** — Subscription period ended without renewal
• **subscription.trial_ending** — Trial period ending (configurable days before)
• **subscription.upgraded** — Plan changed to higher tier
• **subscription.downgraded** — Plan changed to lower tier
• **invoice.past_due** — Invoice not paid by due date
• **payment.failed** — Payment attempt failed (with retry count)
• **payment.succeeded** — Payment collected successfully
• **customer.reinstated** — Previously suspended customer restored
• **customer.suspended** — Customer suspended (manual or automatic)
• **entitlements.granted** — Specific entitlements activated for a customer
• **entitlements.revoked** — Specific entitlements removed from a customer

**Built-in action types:**
• **send_webhook** — POST to a configured URL with event data
• **send_email** — Send a template-based email to the customer
• **grant_entitlements** — Provision access (roles/features) in billing
• **revoke_entitlements** — Deprovision access (roles/features) in billing
• **restrict_access** — Downgrade to a limited access tier
• **notify_external** — Send notification to Slack, PagerDuty, Discord, or any HTTP URL

**Delay:** Rules can have a delay (in days) before executing. This enables grace periods:
• 0 days = execute immediately
• 3 days = wait 3 days after the event before executing
• 14 days = two-week grace period

**Plan scoping:** Rules can be attached to specific plans. A rule attached to the "Enterprise" plan only fires for enterprise subscriptions.

**Example configurations:**

**Dunning workflow (payment recovery):**
1. payment.failed → send_email "Payment Failed" (0-day delay)
2. invoice.past_due → send_email "Payment Reminder" (3-day delay)
3. invoice.past_due → send_email "Final Warning" (7-day delay)
4. invoice.past_due → restrict_access (14-day delay)
5. invoice.past_due → revoke_entitlements + send_email "Access Suspended" (21-day delay)
6. customer.reinstated → grant_entitlements (0-day delay)

**Provisioning workflow (new subscriber):**
1. subscription.created → grant_entitlements (0-day)
2. subscription.created → send_webhook to auth system (0-day)
3. subscription.created → send_email "Welcome" (0-day)

**Upgrade/downgrade workflow:**
1. subscription.upgraded → send_webhook to auth system (0-day)
2. subscription.upgraded → send_email "Upgrade Confirmation" (0-day)
3. subscription.downgraded → send_webhook to auth system (0-day)
4. subscription.downgraded → send_email "Plan Changed" (0-day)

**Ops notifications:**
1. payment.failed (retry count >= 3) → notify_external (Slack) (0-day)
2. subscription.canceled → notify_external (PagerDuty) for enterprise plans only
3. customer.suspended → notify_external (all channels) (0-day)

Configure these rules in the admin console under Operations → Automation Rules.`,
          },
          {
            id: 'enforcement-rule42auth',
            title: 'Integration with rule42-auth',
            content: `If your application uses rule42-auth for identity and access management, billing integration is pre-configured with a bi-directional sync.

**How it works:**
1. **Business roles** in rule42-auth map to **entitlements** in billing (linked via \`auth_business_role_id\`)
2. Click "Sync from Auth" in the Entitlement Mapping screen to pull business roles into billing
3. Map entitlements to plans: Pro plan → check "pro" + "community" entitlements
4. When a user subscribes, billing grants the mapped entitlements → sends webhook to auth → auth assigns the business roles to the user's org members
5. When a subscription is cancelled/expired, billing revokes entitlements → auth removes the business roles

**Setup steps:**
1. Go to Plans page → click a plan → "Configure →" under Features & Entitlements
2. Select "Sync from Auth" enforcement mode
3. Click "Sync from Auth" to pull business roles from your auth system
4. Check the entitlements that should be granted with this plan
5. Click "Save Entitlement Mapping"
6. Verify in Webhooks & Sync that the auth system webhook endpoint is registered

**If you don't want to use auth sync for a specific plan:**
1. Open the plan's Entitlement Mapping
2. Change the enforcement mode to "Outbound Webhook", "API Polling", or "Manual"
3. The plan will no longer sync with rule42-auth — it will use your chosen method instead

**The auth webhook is automatically seeded** during tenant provisioning. If it's missing, run the seed script or create it manually in the Webhooks page.

**Troubleshooting:**
• "No entitlements found" → Click "Sync from Auth" to pull business roles
• Webhook not firing → Check Webhooks & Sync page for the auth endpoint
• Roles not updating → Verify the auth webhook endpoint URL is correct and responsive`,
          },
          {
            id: 'enforcement-complete-setup',
            title: 'Complete Setup Checklist',
            content: `Use this checklist to verify your enforcement integration is working end-to-end.

**Prerequisites:**
☐ At least one plan created in the Plans page
☐ At least one customer created (or will be created via Stripe checkout)
☐ Service token or API credentials available for server-to-server calls

**For Push (Webhook) integration:**
☐ Webhook receiver endpoint deployed and accessible from the internet
☐ Webhook registered in Webhooks & Sync page with correct URL
☐ HMAC signature verification implemented in your receiver
☐ Event handler covers: subscription.created, canceled, upgraded, downgraded
☐ Payment failure handler: payment.failed → warn or restrict
☐ Payment success handler: payment.succeeded → restore access
☐ Error handling: log failures, return 500 to trigger retry
☐ Idempotency: store processed event IDs, skip duplicates
☐ Test: create subscription → verify permissions changed in your auth system
☐ Test: cancel subscription → verify permissions revoked

**For Pull (API Polling) integration:**
☐ Entitlements mapped to plans in the Entitlement Mapping screen
☐ Service token configured in your backend environment
☐ Middleware/hook calling the entitlement check API
☐ Fallback behavior defined (fail open vs. fail closed on API error)
☐ Response caching configured (60–300 seconds recommended)
☐ Test: subscribe customer → check API returns entitled: true
☐ Test: cancel subscription → check API returns entitled: false

**For Automation Rules:**
☐ Rules created for your workflow (dunning, provisioning, notifications)
☐ Rules attached to correct plans (or left unattached for all plans)
☐ Delay values set appropriately (e.g., 7-day grace period)
☐ Email templates created for email actions
☐ External notification URLs configured (Slack, Discord, etc.)
☐ Test: trigger an event and verify the rule executes (check execution history)

**For rule42-auth sync:**
☐ Business roles synced from auth (click "Sync from Auth")
☐ Entitlements mapped to plans
☐ Auth webhook endpoint registered in Webhooks & Sync
☐ Test: subscribe → verify role assigned in auth
☐ Test: cancel → verify role removed in auth

**Monitoring:**
☐ Check Automation Rules page → execution history for failed rules
☐ Check Webhooks & Sync page → delivery logs for failed webhooks
☐ Check Audit Log for entitlement grant/revoke events
☐ Set up a "payment.failed (retry >= 3)" → notify_external rule for ops alerting`,
          },
        ],
      },
      {
        id: 'marketing-features',
        title: 'Marketing Features & Pricing Pages',
        icon: '📄',
        description: 'Create curated feature lists and dynamic pricing pages — display-only, not tied to permissions.',
        sections: [
          {
            id: 'features-overview',
            title: 'Marketing Features vs. Entitlements',
            content: `Marketing features and entitlements serve different purposes:

**Entitlements** (Plans → Entitlement Mapping) control what a customer CAN DO — they gate access to features in your application.

**Marketing features** (Catalog → Marketing Features) control what you SHOW on pricing pages — they describe plan benefits for marketing purposes.

Example:
- Entitlement: \`cloud-sync\` — grants API access to the sync endpoint
- Marketing feature: "Real-time Cloud Sync" with description "Sync your data across all devices instantly"

The same plan can have both:
- Entitlements that enable technical access
- Marketing features that describe the value proposition on the pricing page

**Why separate them?**
- Marketing copy changes frequently; permissions rarely do
- You may want to list "Priority Support" as a feature without it being a technical gate
- Pricing pages need descriptions, icons, and categories that don't belong in the permissions system`,
          },
          {
            id: 'features-api',
            title: 'Marketing Features API',
            content: `CRUD endpoints for managing marketing features programmatically.

**List all features:**
\`GET /api/v1/orgs/{orgId}/marketing-features\`

**Create a feature:**
\`POST /api/v1/orgs/{orgId}/marketing-features\`

**Update a feature:**
\`PATCH /api/v1/orgs/{orgId}/marketing-features/{id}\`

**Delete a feature:**
\`DELETE /api/v1/orgs/{orgId}/marketing-features/{id}\`

**Get features for a plan:**
\`GET /api/v1/orgs/{orgId}/marketing-features/by-plan/{planId}\`

**Assign features to a plan:**
\`PUT /api/v1/orgs/{orgId}/marketing-features/by-plan/{planId}\``,
            codeBlocks: [
              {
                language: 'typescript',
                caption: 'Create a marketing feature and assign to plans',
                code: `// Create a feature
const feature = await fetch('/api/v1/orgs/{orgId}/marketing-features', {
  method: 'POST',
  headers: { Authorization: 'Bearer TOKEN', 'Content-Type': 'application/json' },
  body: JSON.stringify({
    name: 'Real-time Cloud Sync',
    description: 'Sync your data across all devices instantly',
    icon: '☁️',
    category: 'Core',
    displayOrder: 1,
  }),
}).then(r => r.json());

// Assign to the Pro plan
await fetch('/api/v1/orgs/{orgId}/marketing-features/by-plan/{proPlanId}', {
  method: 'PUT',
  headers: { Authorization: 'Bearer TOKEN', 'Content-Type': 'application/json' },
  body: JSON.stringify({
    assignments: [
      { featureId: feature.feature.id, included: true, valueText: 'Unlimited' },
      { featureId: otherFeatureId, included: true, valueText: 'Up to 10' },
      { featureId: enterpriseFeatureId, included: false }, // shown as "—" on pricing page
    ],
  }),
});`,
              },
            ],
          },
          {
            id: 'pricing-pages-api',
            title: 'Pricing Pages API',
            content: `Create and manage dynamic pricing pages. Each page selects which plans to show, what badges to display, and where CTAs link.

**Admin (authenticated) endpoints:**
- \`GET /api/v1/orgs/{orgId}/pricing-pages\` — List all pages
- \`POST /api/v1/orgs/{orgId}/pricing-pages\` — Create a page
- \`PATCH /api/v1/orgs/{orgId}/pricing-pages/{id}\` — Update page settings
- \`DELETE /api/v1/orgs/{orgId}/pricing-pages/{id}\` — Delete a page
- \`GET /api/v1/orgs/{orgId}/pricing-pages/{id}/plans\` — Get plans on a page
- \`PUT /api/v1/orgs/{orgId}/pricing-pages/{id}/plans\` — Set plans on a page
- \`GET /api/v1/orgs/{orgId}/pricing-pages/{id}/render-data\` — Full render data (JSON)

**Public (no auth required) endpoints:**
- \`GET /pricing/{orgId}/{slug}\` — JSON pricing page data (for custom rendering)
- \`GET /pricing/{orgId}/{slug}/html\` — Fully rendered HTML pricing page

Public endpoints only serve **published** pages.`,
            codeBlocks: [
              {
                language: 'typescript',
                caption: 'Create a pricing page with plans and badges',
                code: `// 1. Create the pricing page
const page = await fetch('/api/v1/orgs/{orgId}/pricing-pages', {
  method: 'POST',
  headers: { Authorization: 'Bearer TOKEN', 'Content-Type': 'application/json' },
  body: JSON.stringify({
    slug: 'default',
    title: 'Choose Your Plan',
    subtitle: 'Start free, upgrade anytime. No credit card required.',
    signupUrl: 'https://app.yoursite.com/signup',
    ctaText: 'Get Started Free',
  }),
}).then(r => r.json());

// 2. Add plans with marketing badges
await fetch(\`/api/v1/orgs/{orgId}/pricing-pages/\${page.page.id}/plans\`, {
  method: 'PUT',
  headers: { Authorization: 'Bearer TOKEN', 'Content-Type': 'application/json' },
  body: JSON.stringify({
    plans: [
      { planId: freePlanId, displayOrder: 0 },
      { planId: proPlanId, displayOrder: 1, badge: 'Most Popular', isHighlighted: true },
      { planId: businessPlanId, displayOrder: 2, badge: 'Best Value' },
      { planId: enterprisePlanId, displayOrder: 3, ctaTextOverride: 'Contact Sales', signupUrlOverride: '/contact' },
    ],
  }),
});

// 3. Publish the page
await fetch(\`/api/v1/orgs/{orgId}/pricing-pages/\${page.page.id}\`, {
  method: 'PATCH',
  headers: { Authorization: 'Bearer TOKEN', 'Content-Type': 'application/json' },
  body: JSON.stringify({ isPublished: true }),
});

// Now accessible at: GET /pricing/{orgId}/default (JSON)
// And: GET /pricing/{orgId}/default/html (full HTML page)`,
              },
              {
                language: 'html',
                caption: 'Embed the pricing page in your site',
                code: `<!-- Option A: iframe embed -->
<iframe
  src="https://finance.rule42.ai/pricing/{orgId}/default/html"
  style="width: 100%; min-height: 800px; border: none;"
  title="Pricing"
></iframe>

<!-- Option B: Fetch JSON and render custom -->
<script>
  fetch('https://finance.rule42.ai/pricing/{orgId}/default')
    .then(r => r.json())
    .then(data => {
      // data.page — page config (title, subtitle, ctaText, signupUrl)
      // data.plans[] — array of plan objects with:
      //   .plan — plan details (name, description, interval)
      //   .prices — pricing (amount, currency)
      //   .features — marketing features with valueText
      //   .config — display config (badge, isHighlighted, ctaTextOverride)
      renderPricingPage(data);
    });
</script>`,
              },
            ],
          },
        ],
      },
      {
        id: 'mcp-server',
        title: 'MCP Server (AI Agent Integration)',
        icon: '🤖',
        description: 'Expose billing operations to AI agents via the Model Context Protocol.',
        sections: [
          {
            id: 'mcp-overview',
            title: 'What is the MCP Server?',
            content: `The MCP (Model Context Protocol) server exposes your billing platform as tools that AI agents can discover and use. This enables:

- **VS Code Copilot** to create subscriptions, check entitlements, and manage customers via natural language
- **Claude Desktop** to query billing data and generate reports
- **Custom AI agents** to integrate billing operations into automated workflows

The MCP server communicates via stdio (for local tools) or HTTP/SSE (for remote connections).

**Available tools:** 15 billing operations including customer management, plan operations, subscription lifecycle, usage recording, entitlement checking, and webhook management.

**Configuration:** Settings → MCP Server in the admin console. The page shows:
- Server status (running/stopped)
- Connection endpoints (HTTP/SSE and stdio command)
- Authentication token
- Client configuration examples (VS Code, Claude Desktop)
- Full tool catalog`,
          },
          {
            id: 'mcp-setup',
            title: 'Setting Up MCP Clients',
            content: `Configure your AI agent to connect to the billing MCP server.

**Prerequisites:**
1. Go to Settings → MCP Server in the billing admin
2. Ensure the server shows "Running" status
3. Copy the authentication token

**For VS Code / GitHub Copilot:**
Add to your VS Code settings.json or workspace .vscode/mcp.json:

**For Claude Desktop:**
Add to ~/Library/Application Support/Claude/claude_desktop_config.json (macOS) or %APPDATA%/Claude/claude_desktop_config.json (Windows).

**For custom agents:**
Use the HTTP/SSE endpoint with the auth token in the Authorization header, or spawn the stdio process with the MCP_AUTH_TOKEN environment variable.`,
            codeBlocks: [
              {
                language: 'json',
                caption: 'VS Code MCP configuration (.vscode/mcp.json)',
                code: `{
  "servers": {
    "rule42-billing": {
      "command": "npx",
      "args": ["@rule42/billing-mcp"],
      "env": {
        "BILLING_API_URL": "https://finance.rule42.ai",
        "MCP_AUTH_TOKEN": "<your-token-from-admin>"
      }
    }
  }
}`,
              },
              {
                language: 'json',
                caption: 'Claude Desktop configuration',
                code: `{
  "mcpServers": {
    "rule42-billing": {
      "command": "npx",
      "args": ["@rule42/billing-mcp"],
      "env": {
        "BILLING_API_URL": "https://finance.rule42.ai",
        "MCP_AUTH_TOKEN": "<your-token-from-admin>"
      }
    }
  }
}`,
              },
            ],
          },
          {
            id: 'mcp-tools',
            title: 'Available MCP Tools',
            content: `The billing MCP server exposes the following tools to AI agents:

**Customer Management:**
- \`billing_list_customers\` — List customers with filtering by status/email
- \`billing_get_customer\` — Get detailed customer info + subscriptions + credits
- \`billing_create_customer\` — Create a new customer

**Plan Operations:**
- \`billing_list_plans\` — List all plans with pricing and features
- \`billing_get_plan\` — Get complete plan details

**Subscription Lifecycle:**
- \`billing_list_subscriptions\` — List subscriptions by customer/plan/status
- \`billing_create_subscription\` — Subscribe customer to a plan
- \`billing_cancel_subscription\` — Cancel immediately or at period end

**Billing & Usage:**
- \`billing_list_invoices\` — List invoices with filters
- \`billing_record_usage\` — Record usage events for metered billing
- \`billing_get_credit_balance\` — Check credit balance

**Access & Entitlements:**
- \`billing_check_entitlement\` — Check if customer has a specific entitlement

**Webhooks:**
- \`billing_list_webhooks\` — List registered endpoints
- \`billing_create_webhook\` — Register a new endpoint

**Pricing:**
- \`billing_get_pricing_page\` — Get pricing page data for rendering

**Security:** All MCP tool calls are authenticated. The MCP_AUTH_TOKEN maps to a specific org — the agent can only access data within that org's scope.`,
          },
          {
            id: 'mcp-api',
            title: 'MCP Management API',
            content: `Manage the MCP server programmatically:

**Get server status:**
\`GET /api/v1/orgs/{orgId}/mcp/status\`

Returns: running state, endpoint URL, version, tool list, auth token, connected clients, last activity.

**Start the server:**
\`POST /api/v1/orgs/{orgId}/mcp/start\`

**Stop the server:**
\`POST /api/v1/orgs/{orgId}/mcp/stop\`

**Regenerate auth token:**
\`POST /api/v1/orgs/{orgId}/mcp/regenerate-token\`

Regenerating the token immediately disconnects all active MCP clients. They will need the new token to reconnect.`,
          },
        ],
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // PLATFORM ADMINISTRATION
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'platform',
    title: 'Platform Administration',
    chapters: [
      {
        id: 'tenant-management',
        title: 'Tenant Management',
        icon: '🏢',
        description: 'Managing multi-tenant organizations.',
        sections: [
          {
            id: 'tenant-overview',
            title: 'Multi-Tenant Architecture',
            content: `Rule42 Billing is a multi-tenant platform where each organization (tenant) has isolated billing data. Platform administrators can manage all tenants from the Platform Dashboard.

**Tenant properties:**
• **Auth Org ID** — links to the identity provider organization
• **Commercial Plan** — the plan tier for this tenant (starter, professional, enterprise)
• **Platform Fee** — percentage the platform takes from each transaction
• **Status** — active, suspended, or deactivated
• **Applications** — named billing contexts within a tenant (e.g., "ANDL", "Rule42 Cloud")`,
          },
          {
            id: 'tenant-provisioning',
            title: 'Provisioning a New Tenant',
            content: `New tenants can be provisioned via the admin console, self-service enrollment, or the MCP tool.

Self-service enrollment is available at \`/enroll\` — organizations can sign up and complete Stripe Connect onboarding in one flow.`,
          },
        ],
      },
      {
        id: 'byod',
        title: 'BYOD Database',
        icon: '🗄️',
        description: 'Connecting your own external database for billing data.',
        sections: [
          {
            id: 'byod-overview',
            title: 'Bring Your Own Database',
            content: `BYOD lets tenants connect an external database for billing data replication. This is useful for:

• Running custom reports against billing data in your own analytics stack
• Data residency compliance (keeping billing data in a specific region)
• Integration with existing data warehouses

**Supported databases:**
• PostgreSQL 14+
• TimescaleDB
• ClickHouse`,
          },
          {
            id: 'byod-setup',
            title: 'Setup Process',
            content: `1. Navigate to BYOD Database in the admin console
2. Select your database provider
3. Enter the connection string (encrypted at rest)
4. Click "Validate Connection" to verify connectivity
5. Start the initial data migration
6. Once complete, data is replicated in real-time`,
          },
        ],
      },
      {
        id: 'audit-log',
        title: 'Audit Log',
        icon: '📝',
        description: 'Compliance logging and action history.',
        sections: [
          {
            id: 'audit-coverage',
            title: 'What Gets Logged',
            content: `Every billing action is recorded in the immutable audit log:

• Customer create/update/delete
• Subscription changes (create, upgrade, downgrade, cancel)
• Credit grants, deductions, and pre-authorizations
• Webhook deliveries (inbound and outbound, with response status)
• Admin actions (plan changes, manual overrides, settings updates)
• API key usage and authentication events
• Invoice generation and payment processing`,
          },
          {
            id: 'audit-api',
            title: 'Query Audit Logs',
            content: `Search and filter audit logs programmatically.`,
            codeBlocks: [
              {
                language: 'typescript',
                caption: 'Query audit logs',
                code: `// Filter by event type
GET /api/v1/orgs/<orgId>/audit-logs?event_type=credits.grant&limit=50

// Filter by customer and date range
GET /api/v1/orgs/<orgId>/audit-logs?customer_id=<UUID>&since=2026-06-01

// Filter by actor (who performed the action)
GET /api/v1/orgs/<orgId>/audit-logs?actor_id=<USER_UUID>&limit=100`,
              },
            ],
          },
        ],
      },
      {
        id: 'platform-health',
        title: 'Health & Monitoring',
        icon: '🏥',
        description: 'Platform health checks, noisy neighbors, and GDPR.',
        sections: [
          {
            id: 'health-endpoint',
            title: 'Health Check',
            content: `The billing server exposes a health endpoint for monitoring:`,
            codeBlocks: [
              {
                language: 'bash',
                caption: 'Health check',
                code: `curl https://finance.rule42.ai/health
# { "status": "ok", "version": "1.0.0", "uptime": 86400 }`,
              },
            ],
          },
          {
            id: 'noisy-neighbors',
            title: 'Noisy Neighbor Detection',
            content: `The platform monitors tenant resource consumption to detect noisy neighbors — tenants that consume disproportionate resources. This includes:

• API request rate per tenant
• Database query volume
• Usage event ingestion rate
• Storage consumption

Noisy neighbors are flagged in the Platform Health dashboard with recommended actions (rate limiting, plan upgrade, or direct outreach).`,
          },
          {
            id: 'gdpr',
            title: 'GDPR Data Export',
            content: `For GDPR compliance, platform administrators can export all data for a specific customer. The export includes:

• Customer profile and metadata
• Subscription history
• Usage events
• Invoices and payment records
• Credit transactions
• Audit log entries

Exports are generated as a downloadable archive from the Platform Health page.`,
          },
        ],
      },
    ],
  },
  // ── Stripe Sync & Webhooks ──────────────────────────────────────────
  {
    id: 'stripe-sync',
    title: 'Stripe Sync & Webhooks',
    icon: '🔄',
    chapters: [
      {
        id: 'sync-overview',
        title: 'Sync Overview',
        icon: '📋',
        description: 'How billing stays in constant sync with Stripe.',
        sections: [
          {
            id: 'bidirectional-sync',
            title: 'Bidirectional Sync',
            content: `Rule42 Billing maintains a bidirectional sync with Stripe. The local database is a cache for fast reads; Stripe is the source of truth.

**Stripe → Local (Pull):**
• Customers are pulled and matched by email or stripe_customer_id
• Subscriptions are pulled and matched by stripe_subscription_id in metadata
• Products are pulled and matched by stripe_product_id

**Local → Stripe (Push):**
• Customers without a stripe_customer_id are pushed to Stripe
• The resulting stripe_customer_id is written back to the local row

**Automatic Sync:**
The StripeSyncWorker runs periodically via BullMQ cron and syncs all configured tenants. It auto-heals drift between Stripe and the local database.

**Webhook-Driven Sync:**
Stripe webhook events trigger real-time updates:
• payment_intent.succeeded → Grants credits
• customer.subscription.* → Creates/updates local subscriptions
• entitlements.active_entitlement_summary.updated → Reconciles entitlements`,
          },
          {
            id: 'triggering-a-manual-sync',
            title: 'Triggering a Manual Sync',
            content: `Trigger a full sync via the API or the UI (Admin → Payment Provider → Stripe Sync → "Sync Now").`,
            codeBlocks: [
              {
                language: 'bash',
                caption: 'Sync a single tenant',
                code: `curl -X POST https://finance.rule42.ai/sync/tenant/<authOrgId> \\
  -H "Authorization: Bearer <token>" \\
  -H "Content-Type: application/json" \\
  -d '{}'`,
              },
              {
                language: 'bash',
                caption: 'Sync all configured tenants',
                code: `curl -X POST https://finance.rule42.ai/sync/all \\
  -H "Authorization: Bearer <token>" \\
  -H "Content-Type: application/json" \\
  -d '{}'`,
              },
              {
                language: 'bash',
                caption: 'Check sync status',
                code: `curl https://finance.rule42.ai/sync/status/<authOrgId> \\
  -H "Authorization: Bearer <token>"`,
              },
            ],
          },
        ],
      },
      {
        id: 'webhook-setup',
        title: 'Webhook Configuration',
        icon: '🔗',
        description: 'Register and manage Stripe webhook endpoints.',
        sections: [
          {
            id: 'auto-register-recommended',
            title: 'Auto-Register (Recommended)',
            content: `When a Stripe Connect account is fully onboarded, the webhook endpoint is automatically registered on the connected account. You can also trigger this manually:

1. Go to Admin → Payment Provider → Stripe Sync
2. Click "Register Webhook"

This creates a webhook endpoint on the connected Stripe account pointing to:
\`https://finance.rule42.ai/webhooks/stripe/<authOrgId>\`

The signing secret is stored encrypted in the database automatically.`,
          },
          {
            id: 'manual-webhook-setup',
            title: 'Manual Webhook Setup',
            content: `If auto-registration fails, you can manually configure the webhook:

1. Go to Stripe Dashboard → Webhooks
2. Click "Add endpoint"
3. Set URL to: https://finance.rule42.ai/webhooks/stripe/<authOrgId>
4. Select these events:
   • payment_intent.succeeded
   • payment_intent.payment_failed
   • invoice.paid
   • invoice.payment_failed
   • customer.subscription.created
   • customer.subscription.updated
   • customer.subscription.deleted
   • entitlements.active_entitlement_summary.updated
5. Copy the signing secret (whsec_...)
6. Paste it in Admin → Payment Provider → Webhook Signing Secret`,
          },
          {
            id: 'webhook-url-format',
            title: 'Webhook URL Format',
            content: `Each tenant has a dedicated webhook URL:

\`/webhooks/stripe/<authOrgId>\`

The platform-level webhook at \`/webhooks/stripe\` receives events from the platform account (including destination charge events for connected accounts).`,
            codeBlocks: [
              {
                language: 'text',
                caption: 'Per-tenant webhook URL',
                code: `https://finance.rule42.ai/webhooks/stripe/c09e5839-4196-55f7-bf73-41405d2cbb37`,
              },
            ],
          },
        ],
      },
      {
        id: 'connected-account-routing',
        title: 'Connected Account Routing',
        icon: '🔀',
        description: 'How all Stripe operations route through connected accounts.',
        sections: [
          {
            id: 'routing-overview',
            title: 'Routing Overview',
            content: `All Stripe operations route through the tenant's connected account, not the platform account:

• Customer creation → stripeAccount header
• Payment Intents → transfer_data.destination + application_fee_amount
• Payment Methods → stripeAccount header
• Subscriptions → stripeAccount header
• Products/Prices → stripeAccount header
• Usage/Metering → stripeAccount header

This is handled by resolveTenantStripeContext() which resolves the tenant's Stripe key and connected account ID from their auth_org_id.`,
          },
          {
            id: 'fee-structure',
            title: 'Fee Structure',
            content: `Dual fee structure (configurable in Platform Settings):

• Customer Fee (2% default): Added to the payment total, charged to the buyer
• Vendor Fee (2% default): Taken as application_fee_amount from the connected account
• Stripe Processing (~2.9% + $0.30): Calculated in the total

For Rule42 (customer 0), the platform and connected account are the same, so the vendor fee is effectively a wash.`,
          },
        ],
      },
      {
        id: 'org-provisioning',
        title: 'New Tenant Setup',
        icon: '🚀',
        description: 'How new organizations are provisioned with billing.',
        sections: [
          {
            id: 'automatic-provisioning',
            title: 'Automatic Provisioning',
            content: `When a new org is created in the auth system, the cockpit-server's org provisioning endpoint automatically calls the billing server:

POST /sync/provision

This creates:
1. A billing_tenants row (linked to the auth org)
2. A customers row (the org's primary customer)
3. Syncs the customer to Stripe (if Stripe is configured)

The provisioning is non-blocking — if Stripe isn't configured yet, the customer is created locally and will be synced later.`,
          },
          {
            id: 'manual-provisioning',
            title: 'Manual Provisioning',
            content: `If auto-provisioning didn't run (e.g., during migration), you can manually provision:`,
            codeBlocks: [
              {
                language: 'bash',
                caption: 'Provision billing for an org',
                code: `curl -X POST https://finance.rule42.ai/sync/provision \\
  -H "Authorization: Bearer <token>" \\
  -H "Content-Type: application/json" \\
  -d '{
    "authOrgId": "<org-uuid>",
    "orgName": "Acme Corp",
    "plan": "community",
    "adminEmail": "admin@acme.com"
  }'`,
              },
            ],
          },
          {
            id: 'setup-checklist',
            title: 'Setup Checklist',
            content: `For a new billing tenant:

1. ✅ Org is provisioned (billing tenant + customer created)
2. ✅ Stripe Connect Express onboarding completed
3. ✅ Stripe keys configured (test or live)
4. ✅ Webhook endpoint registered (auto or manual)
5. ✅ Initial sync completed (customers, subscriptions, products pulled)
6. ✅ Test payment processed successfully`,
          },
        ],
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Flattened index for search and navigation
// ---------------------------------------------------------------------------

export interface FlatGuideEntry {
  categoryId: string;
  categoryTitle: string;
  chapterId: string;
  chapterTitle: string;
  chapterIcon: string;
  sectionId: string;
  sectionTitle: string;
}

export function buildFlatIndex(): FlatGuideEntry[] {
  const entries: FlatGuideEntry[] = [];
  for (const cat of GUIDE_CATEGORIES) {
    for (const ch of cat.chapters) {
      for (const sec of ch.sections) {
        entries.push({
          categoryId: cat.id,
          categoryTitle: cat.title,
          chapterId: ch.id,
          chapterTitle: ch.title,
          chapterIcon: ch.icon,
          sectionId: sec.id,
          sectionTitle: sec.title,
        });
      }
    }
  }
  return entries;
}
